'use client';

import { useEffect, useState, useCallback } from 'react';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { workspaceApi } from '@/lib/api/workspace/workspaceApi';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { toast } from 'sonner';
import type { Status } from 'dico-types';
import { PickListSection } from '@/components/settings/PickListSection';

interface StatusesTabContentProps {
  workspaceId: string;
}

export function StatusesTabContent({ workspaceId }: StatusesTabContentProps) {
  const refreshStatuses = useWorkspaceStore((s) => s.refreshStatuses);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded'>('idle');

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      const statusesRes = await workspaceApi.getWorkspaceStatuses(workspaceId);
      setStatuses(statusesRes.data ?? []);
      setLoadState('loaded');
    } catch {
      toast.error('Failed to load statuses');
      setLoadState('loaded');
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadState === 'loading') {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <MonoIcon type="Loader2" className="text-muted-foreground animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-3xl mx-auto min-h-full flex-1 space-y-6 px-8 py-10">
      <PickListSection
        title="Statuses"
        description="Board columns and list groups (for example New, In progress, and Done)."
        items={statuses}
        showIcon
        defaultNewItemName="New status"
        onAdd={async (payload) => {
          const res = await workspaceApi.createStatus(workspaceId, payload);
          if (res.success && res.data) {
            setStatuses((prev) => [...prev, res.data!]);
            if (refreshStatuses) refreshStatuses(workspaceId);
            toast.success('Status added');
            return res.data.id;
          }
          toast.error(res.error ?? 'Failed to add status');
          return null;
        }}
        onUpdate={async (id, payload) => {
          const res = await workspaceApi.updateStatus(workspaceId, id, payload);
          if (res.success && res.data) {
            setStatuses((prev) => prev.map((s) => (s.id === id ? res.data! : s)));
            if (refreshStatuses) refreshStatuses(workspaceId);
            toast.success('Status updated');
          } else {
            toast.error(res.error ?? 'Failed to update status');
          }
        }}
        onDelete={async (id) => {
          const res = await workspaceApi.deleteStatus(workspaceId, id);
          if (res.success) {
            setStatuses((prev) => prev.filter((s) => s.id !== id));
            if (refreshStatuses) refreshStatuses(workspaceId);
            toast.success('Status removed');
          } else {
            toast.error(res.error ?? 'Failed to remove status');
          }
        }}
      />
    </div>
  );
}
